import { useCompanyVariant } from './useCompanyVariant';
import type { OrganizationVariant } from '../types/organization';

/**
 * Vocabulaire métier swappable selon le variant de l'organisation courante.
 *
 * Principe : aucune logique applicative ne change selon le variant — seuls
 * les libellés affichés sont remplacés. Une coopérative est, du point de
 * vue du domaine, une PME multi-succursale ; on remplace juste les mots.
 *
 * Usage :
 * ```tsx
 * const vocab = useEntityVocabulary();
 * return <h2>Vos {vocab.branches}</h2>;
 * // → "Vos succursales" pour une PME
 * // → "Vos coopérants" pour une coopérative
 * ```
 */
export interface EntityVocabulary {
  variant: OrganizationVariant;
  /** L'entité ("entreprise" / "coopérative") */
  entity: string;
  entityCapitalized: string;
  /** Avec article défini : "l'entreprise" / "la coopérative" */
  entityWithArticle: string;
  /** Avec possessif : "votre entreprise" / "votre coopérative" */
  yourEntity: string;
  /** Singulier — membre / employé / coopérant */
  member: string;
  members: string;
  /** Singulier — succursale (= coopérant en mode coop) */
  branch: string;
  branchCapitalized: string;
  branches: string;
  branchesCapitalized: string;
  /** Verbe d'action : "Ajouter une succursale" / "Ajouter un coopérant" */
  addBranchLabel: string;
  newBranchLabel: string;
  emptyBranchesLabel: string;
  createBranchLabel: string;
  branchNameLabel: string;
  /** Label commercial */
  segmentLabel: string;
}

const STANDARD: EntityVocabulary = {
  variant: 'standard',
  entity: 'entreprise',
  entityCapitalized: 'Entreprise',
  entityWithArticle: 'l\'entreprise',
  yourEntity: 'votre entreprise',
  member: 'employé',
  members: 'employés',
  branch: 'succursale',
  branchCapitalized: 'Succursale',
  branches: 'succursales',
  branchesCapitalized: 'Succursales',
  addBranchLabel: 'Ajouter une succursale',
  newBranchLabel: 'Nouvelle succursale',
  emptyBranchesLabel: 'Aucune succursale',
  createBranchLabel: 'Créer une succursale',
  branchNameLabel: 'Nom de la succursale',
  segmentLabel: 'PME',
};

const COOPERATIVE: EntityVocabulary = {
  variant: 'cooperative',
  entity: 'coopérative',
  entityCapitalized: 'Coopérative',
  entityWithArticle: 'la coopérative',
  yourEntity: 'votre coopérative',
  member: 'coopérant',
  members: 'coopérants',
  branch: 'coopérant',
  branchCapitalized: 'Coopérant',
  branches: 'coopérants',
  branchesCapitalized: 'Coopérants',
  addBranchLabel: 'Ajouter un coopérant',
  newBranchLabel: 'Nouveau coopérant',
  emptyBranchesLabel: 'Aucun coopérant',
  createBranchLabel: 'Ajouter un coopérant',
  branchNameLabel: 'Nom du coopérant',
  segmentLabel: 'Coopérative',
};

export function getVocabularyForVariant(variant: OrganizationVariant): EntityVocabulary {
  return variant === 'cooperative' ? COOPERATIVE : STANDARD;
}

/**
 * Hook React — retourne le vocabulaire pour l'organisation courante.
 * Stable entre rendus tant que le variant ne change pas.
 */
export function useEntityVocabulary(): EntityVocabulary {
  const variant = useCompanyVariant();
  return getVocabularyForVariant(variant);
}
